import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Alert, AlertDescription } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Skeleton } from '@/ui/base/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';
import { Textarea } from '@/ui/base/textarea';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { PageHeader } from '@/ui/PageHeader';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { StatusBadge, type StatusVariant } from '@/ui/StatusBadge';
import {
  AlertTriangle,
  ArrowLeft,
  Calculator,
  ClipboardCheck,
  Download,
  FileText,
  History,
  MessageSquare,
  RefreshCw,
} from 'lucide-react';

import {
  useMedicaoDetalhe,
  useMedicaoItens,
  useMedicaoAjustes,
  useMedicaoObservacoes,
  useMedicaoSofTimeline,
  useMedicaoRevisoes,
  useMedicaoPedidosPortal,
  useMedicaoDocumentos,
  useMedicaoChecklist,
  useMedicaoCapacidade,
  useMedicaoHistorico,
  useMutacoesMedicao,
} from '../hooks/useMedicoes';
import type {
  MedicaoItemLinha,
  AjusteMedicao,
  RevisaoMedicao,
  PedidoPortalMedicao,
  DocumentoMedicao,
  ChecklistDocumentoObrigatorio,
  CapacidadeBancadaLinha,
  AlteracaoHistoricoMedicao,
  StatusMedicao,
} from '../types';

const ROTULO_STATUS: Record<StatusMedicao, { label: string; variant: StatusVariant }> = {
  RASCUNHO: { label: 'Rascunho', variant: 'rascunho' },
  PROCESSADA: { label: 'Processada', variant: 'pendente' },
  APROVADA: { label: 'Aprovada', variant: 'aprovado' },
  EXPORTADA_SOF: { label: 'Exportada SOF', variant: 'aprovado' },
  PAGA: { label: 'Paga', variant: 'pago' },
};

function formatarData(iso: string | null, comHora = false): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat(
    'pt-BR',
    comHora ? { dateStyle: 'short', timeStyle: 'short' } : { dateStyle: 'short' },
  ).format(new Date(iso));
}

function formatarCompetencia(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
}

/**
 * `/operacao/financeiro/medicoes/:id` (FIN-002) — recálculo e conferência
 * completa de uma medição, com as 9 abas do simulador de ateste.
 */
export function MedicaoDetalhePage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const medicao = useMedicaoDetalhe(id);
  const mutacoes = useMutacoesMedicao(id);

  const [editandoInfo, definirEditandoInfo] = useState(false);
  const [previsaoCredito, definirPrevisaoCredito] = useState('');
  const [obsFin, definirObsFin] = useState('');
  const [obsDre, definirObsDre] = useState('');

  if (medicao.isError) {
    return (
      <ErrorState
        variant="load-failed"
        primaryAction={{ label: 'Tentar novamente', onClick: () => void medicao.refetch() }}
      />
    );
  }

  if (medicao.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!medicao.data) {
    return <ErrorState variant="not-found" title="Medição não encontrada" />;
  }

  const m = medicao.data;
  const status = ROTULO_STATUS[m.status];

  function abrirEdicaoInfo() {
    definirPrevisaoCredito(m.previsaoCredito ?? '');
    definirObsFin(m.obsFin ?? '');
    definirObsDre(m.obsDre ?? '');
    definirEditandoInfo(true);
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate('/operacao/financeiro/medicoes')}>
        <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" /> Voltar
      </Button>

      <PageHeader
        eyebrow="Financeiro"
        title={`Medição — ${formatarCompetencia(m.mesReferencia)}`}
        description={m.credenciadoNome}
      >
        <StatusBadge status={status.variant} label={status.label} />
        <Button
          variant="outline"
          disabled={m.numeroDocumentos === 0}
          title="Exportação PDF ainda não está disponível"
        >
          <FileText className="mr-2 h-4 w-4" aria-hidden="true" /> Demonstrativo PDF
        </Button>
        <Button variant="outline">
          <MessageSquare className="mr-2 h-4 w-4" aria-hidden="true" /> Solicitar Revisão
        </Button>
        {m.calculoDesatualizado && (
          <Button>
            <Calculator className="mr-2 h-4 w-4" aria-hidden="true" /> Recalcular
          </Button>
        )}
      </PageHeader>

      {m.calculoDesatualizado && (
        <Alert variant="destructive">
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>
            Cálculo desatualizado: esta medição foi processada com uma versão anterior da tabela de
            preços. Recalcule antes de aprovar.
          </AlertDescription>
        </Alert>
      )}

      {m.alertaCompetenciaIrInssMeses != null && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>
            Competência de recolhimento IR/INSS com {m.alertaCompetenciaIrInssMeses} mês(es) de
            defasagem.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardContent className="grid gap-4 p-6 sm:grid-cols-2">
            <div>
              <p className="text-sm text-muted-foreground">Credenciado</p>
              <p className="font-medium">{m.credenciadoNome}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Termo SEI</p>
              <p className="font-mono text-sm font-medium">{m.termoSei ?? '—'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Valor Bruto</p>
              <p className="font-medium">
                <MoneyDisplay value={m.valorBruto} />
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Valor Líquido</p>
              <p className="font-medium">
                <MoneyDisplay value={m.valorLiquido} />
              </p>
            </div>
            {parseFloat(m.retencoesPf) > 0 && (
              <div>
                <p className="text-sm text-muted-foreground">Retenções PF</p>
                <p className="font-medium">
                  <MoneyDisplay value={m.retencoesPf} />
                </p>
              </div>
            )}
            {(parseFloat(m.descontosMultas) > 0 || parseFloat(m.descontosCobrancas) > 0) && (
              <div>
                <p className="text-sm text-muted-foreground">Descontos (multas + cobranças)</p>
                <p className="font-medium">
                  <MoneyDisplay
                    value={(
                      parseFloat(m.descontosMultas) + parseFloat(m.descontosCobrancas)
                    ).toFixed(2)}
                  />
                </p>
              </div>
            )}
            <div>
              <p className="text-sm text-muted-foreground">Processado em</p>
              <p className="font-medium">{formatarData(m.processadoEm)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Aprovado em</p>
              <p className="font-medium">{formatarData(m.aprovadoEm)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-3 p-6">
            <h3 className="font-medium">Retenções e Pós-fechamento</h3>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Risco fiscal</span>
              <Badge
                variant={
                  m.riscoFiscal === 'CRITICO'
                    ? 'destructive'
                    : m.riscoFiscal === 'ATENCAO'
                      ? 'secondary'
                      : 'outline'
                }
              >
                {m.riscoFiscal === 'CRITICO'
                  ? 'Crítico'
                  : m.riscoFiscal === 'ATENCAO'
                    ? 'Atenção'
                    : 'Regular'}
              </Badge>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Divergências potenciais</span>
              <span className="font-medium">{m.divergenciasPotenciais}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-3 p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">Informações para o Demonstrativo</h3>
              <Button variant="outline" size="sm" onClick={abrirEdicaoInfo}>
                Editar informações
              </Button>
            </div>
            <div className="grid gap-2 text-sm">
              <p>
                <span className="text-muted-foreground">Previsão de Crédito: </span>
                {formatarData(m.previsaoCredito)}
              </p>
              <p>
                <span className="text-muted-foreground">OBS FIN: </span>
                {m.obsFin ?? '—'}
              </p>
              <p>
                <span className="text-muted-foreground">OBS DRE: </span>
                {m.obsDre ?? '—'}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-2 p-6 text-sm">
            <h3 className="font-medium">Trilha SME e Apuração</h3>
            <p>
              <span className="text-muted-foreground">Lote institucional: </span>
              {m.loteInstitucionalStatus ?? '—'}
            </p>
            <p>
              <span className="text-muted-foreground">Revisões registradas: </span>
              {m.numeroRevisoes}
            </p>
            <p>
              <span className="text-muted-foreground">Documentos vinculados: </span>
              {m.numeroDocumentos}
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="itens">
        <TabsList className="flex-wrap">
          <TabsTrigger value="itens">Itens</TabsTrigger>
          <TabsTrigger value="ajustes">Ajustes</TabsTrigger>
          <TabsTrigger value="sof">SOF</TabsTrigger>
          <TabsTrigger value="revisoes">Revisões</TabsTrigger>
          <TabsTrigger value="portal">Pedidos do Portal</TabsTrigger>
          <TabsTrigger value="documentos">Documentos</TabsTrigger>
          <TabsTrigger value="checklist">Checklist pagto.</TabsTrigger>
          <TabsTrigger value="capacidade">Capacidade aplicada</TabsTrigger>
          <TabsTrigger value="historico">Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="itens">
          <AbaItens medicaoId={id} />
        </TabsContent>
        <TabsContent value="ajustes">
          <AbaAjustes medicaoId={id} />
        </TabsContent>
        <TabsContent value="sof">
          <AbaSof medicaoId={id} />
        </TabsContent>
        <TabsContent value="revisoes">
          <AbaRevisoes medicaoId={id} novaRevisao={mutacoes.novaRevisao} />
        </TabsContent>
        <TabsContent value="portal">
          <AbaPedidosPortal medicaoId={id} responder={mutacoes.responderPedidoPortal} />
        </TabsContent>
        <TabsContent value="documentos">
          <AbaDocumentos medicaoId={id} enviar={mutacoes.enviarDocumento} />
        </TabsContent>
        <TabsContent value="checklist">
          <AbaChecklist medicaoId={id} atualizar={mutacoes.atualizarChecklist} />
        </TabsContent>
        <TabsContent value="capacidade">
          <AbaCapacidade medicaoId={id} />
        </TabsContent>
        <TabsContent value="historico">
          <AbaHistorico medicaoId={id} />
        </TabsContent>
      </Tabs>

      <ResponsiveModal
        open={editandoInfo}
        onOpenChange={definirEditandoInfo}
        title="Editar informações do demonstrativo"
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="med-previsao">Previsão de Crédito</Label>
            <Input
              id="med-previsao"
              type="date"
              value={previsaoCredito}
              onChange={(e) => definirPrevisaoCredito(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="med-obs-fin">OBS FIN</Label>
            <Textarea
              id="med-obs-fin"
              value={obsFin}
              onChange={(e) => definirObsFin(e.target.value)}
              rows={2}
            />
          </div>
          <div>
            <Label htmlFor="med-obs-dre">OBS DRE</Label>
            <Textarea
              id="med-obs-dre"
              value={obsDre}
              onChange={(e) => definirObsDre(e.target.value)}
              rows={2}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => definirEditandoInfo(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() =>
                mutacoes.editarInformacoesDemonstrativo.mutate(undefined, {
                  onSuccess: () => definirEditandoInfo(false),
                })
              }
              disabled={mutacoes.editarInformacoesDemonstrativo.isPending}
            >
              Salvar
            </Button>
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
}

function AbaItens({ medicaoId }: { medicaoId: string }) {
  const { data, isLoading, isError, refetch } = useMedicaoItens(medicaoId);
  const itens = data ?? [];
  const totalBruto = itens.reduce((soma, i) => soma + parseFloat(i.totalBruto), 0);

  const colunas: DataTableColumn<MedicaoItemLinha & { id: string }>[] = [
    { header: 'OS', accessorKey: 'numeroOs' },
    { header: 'Tipo Veículo', accessorKey: 'tipoVeiculo' },
    { header: 'Km', accessorKey: 'km', cellClassName: 'text-right', headerClassName: 'text-right' },
    {
      header: 'Dias',
      accessorKey: 'dias',
      cellClassName: 'text-right',
      headerClassName: 'text-right',
    },
    {
      header: 'Total',
      cell: (l) => <MoneyDisplay value={l.totalBruto} />,
      cellClassName: 'text-right',
      headerClassName: 'text-right',
    },
  ];

  if (isError) {
    return (
      <ErrorState
        variant="load-failed"
        primaryAction={{ label: 'Tentar novamente', onClick: () => void refetch() }}
      />
    );
  }

  return (
    <div className="space-y-3">
      <DataTable
        columns={colunas}
        data={itens.map((i) => ({ ...i, id: i.ordemServicoId }))}
        isLoading={isLoading}
        emptyTitle="Nenhum item"
        emptyDescription="Esta medição ainda não tem itens calculados."
        ariaLabel="Itens da medição"
      />
      {itens.length > 0 && (
        <p className="text-right text-sm font-medium">
          Total Bruto: <MoneyDisplay value={totalBruto.toFixed(2)} />
        </p>
      )}
    </div>
  );
}

function AbaAjustes({ medicaoId }: { medicaoId: string }) {
  const ajustes = useMedicaoAjustes(medicaoId);
  const observacoes = useMedicaoObservacoes(medicaoId);

  const colunasAjuste: DataTableColumn<AjusteMedicao & { id: string }>[] = [
    { header: 'Quando', cell: (l) => formatarData(l.quando, true) },
    { header: 'Tipo', cell: (l) => <Badge variant="outline">{l.tipo}</Badge> },
    { header: 'OS', cell: (l) => l.ordemServicoId ?? '—' },
    {
      header: 'Valor',
      cell: (l) => <MoneyDisplay value={l.valor} />,
      cellClassName: 'text-right',
      headerClassName: 'text-right',
    },
    {
      header: 'Status',
      cell: (l) => (
        <Badge variant={l.status === 'ATIVO' ? 'default' : 'secondary'}>{l.status}</Badge>
      ),
    },
    { header: 'Motivo', accessorKey: 'motivo' },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="space-y-3 p-4">
          <h3 className="font-medium">Ajustes da medição</h3>
          <DataTable
            columns={colunasAjuste}
            data={(ajustes.data ?? []).map((a) => ({ ...a, id: a.id }))}
            isLoading={ajustes.isLoading}
            emptyTitle="Sem ajustes registrados"
            ariaLabel="Ajustes da medição"
          />
        </CardContent>
      </Card>
      <Card>
        <CardContent className="space-y-3 p-4">
          <h3 className="font-medium">Observações estruturadas</h3>
          {(observacoes.data ?? []).length === 0 ? (
            <EmptyState
              title="Sem observações"
              description="Nenhuma observação estruturada registrada ainda."
            />
          ) : (
            <ul className="space-y-2">
              {(observacoes.data ?? []).map((o) => (
                <li key={o.id} className="rounded-lg border border-border p-3 text-sm">
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline">Origem: {o.origem}</Badge>
                    <Badge variant="outline">Motivo: {o.motivo}</Badge>
                    <Badge variant="outline">Destinatário: {o.destinatario}</Badge>
                  </div>
                  <p className="mt-2">{o.texto}</p>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AbaSof({ medicaoId }: { medicaoId: string }) {
  const { data, isLoading } = useMedicaoSofTimeline(medicaoId);

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (!data)
    return (
      <EmptyState
        title="Sem dados de SOF"
        description="Esta medição ainda não foi encaminhada ao SOF."
      />
    );

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="space-y-2 p-4">
          <h3 className="font-medium">Status SOF</h3>
          <Badge>{data.estagio}</Badge>
          {data.rejeicaoMotivo && (
            <Alert variant="destructive">
              <AlertDescription>{data.rejeicaoMotivo}</AlertDescription>
            </Alert>
          )}
          <Alert>
            <AlertDescription>
              Modo simulação: o envio ao SOF ainda é manual nesta etapa.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <h3 className="mb-3 font-medium">Linha do tempo</h3>
          <ol className="space-y-2">
            {data.marcos.map((marco) => (
              <li key={marco.nome} className="flex items-center justify-between text-sm">
                <span>{marco.nome}</span>
                <span className="text-muted-foreground">{formatarData(marco.dataHora, true)}</span>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      {data.empenho && (
        <Card>
          <CardContent className="grid gap-3 p-4 sm:grid-cols-2">
            <h3 className="col-span-full font-medium">Empenho</h3>
            <p>
              <span className="text-muted-foreground">Número NE: </span>
              {data.empenho.numero}
            </p>
            <p>
              <span className="text-muted-foreground">Data: </span>
              {formatarData(data.empenho.data)}
            </p>
            <p>
              <span className="text-muted-foreground">Valor: </span>
              <MoneyDisplay value={data.empenho.valor} />
            </p>
            <p>
              <span className="text-muted-foreground">Dotação: </span>
              {data.empenho.dotacao}
            </p>
          </CardContent>
        </Card>
      )}

      {data.loteEmpenho && (
        <Card>
          <CardContent className="grid gap-3 p-4 sm:grid-cols-2">
            <h3 className="col-span-full font-medium">Lote de empenho</h3>
            <p>
              <span className="text-muted-foreground">Número do lote: </span>
              {data.loteEmpenho.numeroLote}
            </p>
            <p>
              <span className="text-muted-foreground">Protocolo SOF: </span>
              {data.loteEmpenho.protocoloSof}
            </p>
            <p>
              <span className="text-muted-foreground">Status: </span>
              {data.loteEmpenho.status}
            </p>
          </CardContent>
        </Card>
      )}

      {data.arquivos.length > 0 && (
        <Card>
          <CardContent className="space-y-2 p-4">
            <h3 className="font-medium">Arquivos SOF gerados</h3>
            <ul className="divide-y divide-border">
              {data.arquivos.map((a) => (
                <li key={a.nome} className="flex items-center justify-between py-2 text-sm">
                  <span>
                    {a.nome} ({a.formato}) — {formatarData(a.dataGeracao)} —{' '}
                    <MoneyDisplay value={a.valorExportado} />
                  </span>
                  <Button variant="outline" size="sm">
                    <Download className="mr-1 h-4 w-4" aria-hidden="true" /> Baixar
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function AbaRevisoes({
  medicaoId,
  novaRevisao,
}: {
  medicaoId: string;
  novaRevisao: ReturnType<typeof useMutacoesMedicao>['novaRevisao'];
}) {
  const { data, isLoading } = useMedicaoRevisoes(medicaoId);
  const [aberto, definirAberto] = useState(false);
  const [tipo, definirTipo] = useState('CORRECAO_VALORES');
  const [motivo, definirMotivo] = useState('');

  const revisoes: (RevisaoMedicao & { id: string })[] = (data ?? []).map((r) => ({
    ...r,
    id: r.id,
  }));

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => definirAberto(true)}>
          <ClipboardCheck className="mr-2 h-4 w-4" aria-hidden="true" /> Nova Revisão
        </Button>
      </div>
      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : revisoes.length === 0 ? (
        <EmptyState
          title="Nenhuma revisão"
          description="Nenhuma revisão foi solicitada para esta medição."
        />
      ) : (
        <div className="space-y-3">
          {revisoes.map((r) => (
            <Card key={r.id}>
              <CardContent className="space-y-2 p-4 text-sm">
                <div className="flex items-center justify-between">
                  <Badge
                    variant={
                      r.status === 'APROVADA'
                        ? 'default'
                        : r.status === 'REJEITADA'
                          ? 'destructive'
                          : 'secondary'
                    }
                  >
                    {r.status}
                  </Badge>
                  <span className="text-muted-foreground">{r.tipo}</span>
                </div>
                <p>{r.motivo}</p>
                <p className="text-muted-foreground">
                  Original: <MoneyDisplay value={r.valorOriginal} /> → Revisado:{' '}
                  <MoneyDisplay value={r.valorRevisado} />
                </p>
                {r.parecer && <p className="italic">{r.parecer}</p>}
                {r.status === 'PENDENTE' && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline">
                      Aprovar
                    </Button>
                    <Button size="sm" variant="outline">
                      Rejeitar
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ResponsiveModal open={aberto} onOpenChange={definirAberto} title="Nova revisão">
        <div className="space-y-4">
          <div>
            <Label>Tipo</Label>
            <Select value={tipo} onValueChange={definirTipo}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CORRECAO_VALORES">Correção de Valores</SelectItem>
                <SelectItem value="COMPLEMENTO">Complemento</SelectItem>
                <SelectItem value="ESTORNO">Estorno</SelectItem>
                <SelectItem value="RECALCULO">Recálculo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="rev-motivo">Motivo</Label>
            <Textarea
              id="rev-motivo"
              value={motivo}
              onChange={(e) => definirMotivo(e.target.value)}
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => definirAberto(false)}>
              Cancelar
            </Button>
            <Button
              disabled={motivo.trim().length === 0 || novaRevisao.isPending}
              onClick={() =>
                novaRevisao.mutate(undefined, { onSuccess: () => definirAberto(false) })
              }
            >
              Enviar
            </Button>
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
}

function AbaPedidosPortal({
  medicaoId,
  responder,
}: {
  medicaoId: string;
  responder: ReturnType<typeof useMutacoesMedicao>['responderPedidoPortal'];
}) {
  const { data, isLoading } = useMedicaoPedidosPortal(medicaoId);
  const [respondendo, definirRespondendo] = useState<string | null>(null);
  const [resposta, definirResposta] = useState('');
  const [statusResposta, definirStatusResposta] = useState('EM_ANALISE');

  const pedidos: (PedidoPortalMedicao & { id: string })[] = (data ?? []).map((p) => ({
    ...p,
    id: p.id,
  }));

  if (isLoading) return <Skeleton className="h-32 w-full" />;
  if (pedidos.length === 0) {
    return (
      <EmptyState
        title="Sem pedidos do portal"
        description="Nenhum pedido de revisão foi enviado pelo credenciado."
      />
    );
  }

  return (
    <div className="space-y-3">
      {pedidos.map((p) => (
        <Card key={p.id}>
          <CardContent className="space-y-2 p-4 text-sm">
            <div className="flex items-center justify-between">
              <Badge
                variant={
                  p.status === 'ACEITA'
                    ? 'default'
                    : p.status === 'REJEITADA'
                      ? 'destructive'
                      : 'secondary'
                }
              >
                {p.status}
              </Badge>
              <span className="text-muted-foreground">{p.origem}</span>
            </div>
            <p>{p.motivo}</p>
            {p.resposta && <p className="italic">Resposta: {p.resposta}</p>}
            {(p.status === 'PENDENTE' || p.status === 'EM_ANALISE') && (
              <Button size="sm" variant="outline" onClick={() => definirRespondendo(p.id)}>
                Responder
              </Button>
            )}
          </CardContent>
        </Card>
      ))}

      <ResponsiveModal
        open={!!respondendo}
        onOpenChange={(v) => !v && definirRespondendo(null)}
        title="Responder pedido"
      >
        <div className="space-y-4">
          <div>
            <Label>Status</Label>
            <Select value={statusResposta} onValueChange={definirStatusResposta}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="EM_ANALISE">Em análise</SelectItem>
                <SelectItem value="ACEITA">Aceita</SelectItem>
                <SelectItem value="REJEITADA">Rejeitada</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="pedido-resposta">Resposta</Label>
            <Textarea
              id="pedido-resposta"
              value={resposta}
              onChange={(e) => definirResposta(e.target.value)}
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => definirRespondendo(null)}>
              Cancelar
            </Button>
            <Button
              disabled={responder.isPending}
              onClick={() =>
                responder.mutate(undefined, { onSuccess: () => definirRespondendo(null) })
              }
            >
              Enviar resposta
            </Button>
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
}

function AbaDocumentos({
  medicaoId,
  enviar,
}: {
  medicaoId: string;
  enviar: ReturnType<typeof useMutacoesMedicao>['enviarDocumento'];
}) {
  const { data, isLoading } = useMedicaoDocumentos(medicaoId);
  const [aberto, definirAberto] = useState(false);

  const colunas: DataTableColumn<DocumentoMedicao & { id: string }>[] = [
    { header: 'Tipo', cell: (l) => <Badge variant="outline">{l.tipo}</Badge> },
    { header: 'Arquivo', accessorKey: 'arquivoNome' },
    { header: 'Enviado em', cell: (l) => formatarData(l.enviadoEm, true) },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => definirAberto(true)}>Enviar Documento</Button>
      </div>
      <DataTable
        columns={colunas}
        data={(data ?? []).map((d) => ({ ...d, id: d.id }))}
        isLoading={isLoading}
        emptyTitle="Nenhum documento"
        ariaLabel="Documentos da medição"
      />
      <ResponsiveModal open={aberto} onOpenChange={definirAberto} title="Enviar documento">
        <div className="space-y-4">
          <div>
            <Label>Tipo</Label>
            <Select defaultValue="NOTA_FISCAL">
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NOTA_FISCAL">Nota Fiscal</SelectItem>
                <SelectItem value="DEMONSTRATIVO_ASSINADO">Demonstrativo Assinado</SelectItem>
                <SelectItem value="PRESTACAO_CONTAS">Prestação de Contas</SelectItem>
                <SelectItem value="COMPROVANTE_PAGAMENTO">Comprovante de Pagamento</SelectItem>
                <SelectItem value="RECIBO">Recibo</SelectItem>
                <SelectItem value="OUTRO">Outro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="doc-arquivo">Arquivo</Label>
            <Input id="doc-arquivo" type="file" accept=".pdf,.jpg,.png,.doc,.xls" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => definirAberto(false)}>
              Cancelar
            </Button>
            <Button
              disabled={enviar.isPending}
              onClick={() => enviar.mutate(undefined, { onSuccess: () => definirAberto(false) })}
            >
              Enviar
            </Button>
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
}

function AbaChecklist({
  medicaoId,
  atualizar,
}: {
  medicaoId: string;
  atualizar: ReturnType<typeof useMutacoesMedicao>['atualizarChecklist'];
}) {
  const { data, isLoading } = useMedicaoChecklist(medicaoId);
  const itens: (ChecklistDocumentoObrigatorio & { id: string })[] = (data ?? []).map((c) => ({
    ...c,
    id: c.id,
  }));

  const aprovados = itens.filter((i) => i.status === 'APROVADO').length;
  const vencidos = itens.filter((i) => i.status === 'VENCIDO').length;
  const rejeitados = itens.filter((i) => i.status === 'REJEITADO').length;
  const pendencias = itens.some((i) => i.status !== 'APROVADO');

  if (isLoading) return <Skeleton className="h-32 w-full" />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Badge variant="outline">
          {aprovados}/{itens.length} aprovados
        </Badge>
        {vencidos > 0 && <Badge variant="destructive">{vencidos} vencido(s)</Badge>}
        {rejeitados > 0 && <Badge variant="destructive">{rejeitados} rejeitado(s)</Badge>}
      </div>

      {pendencias && (
        <Alert variant="destructive">
          <AlertDescription>
            Pagamento não pode ser autorizado enquanto houver documentos não aprovados ou vencidos.
          </AlertDescription>
        </Alert>
      )}

      {itens.length === 0 ? (
        <EmptyState
          title="Sem itens"
          description="Checklist ainda não foi inicializado para esta medição."
        />
      ) : (
        <div className="space-y-2">
          {itens.map((item) => (
            <Card key={item.id}>
              <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4 text-sm">
                <div>
                  <p className="font-medium">{item.nomeDocumento}</p>
                  {item.validade && (
                    <p className="text-muted-foreground">Validade: {formatarData(item.validade)}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant={
                      item.status === 'APROVADO'
                        ? 'default'
                        : item.status === 'VENCIDO' || item.status === 'REJEITADO'
                          ? 'destructive'
                          : 'secondary'
                    }
                  >
                    {item.status}
                  </Badge>
                  <Button variant="outline" size="sm">
                    Abrir anexo
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => atualizar.mutate()}>
                    Aprovar
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => atualizar.mutate()}>
                    Rejeitar
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => atualizar.mutate()}>
                    Editar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function AbaCapacidade({ medicaoId }: { medicaoId: string }) {
  const { data, isLoading } = useMedicaoCapacidade(medicaoId);
  const porOs = new Map<string, CapacidadeBancadaLinha[]>();
  for (const linha of data ?? []) {
    porOs.set(linha.ordemServicoId, [...(porOs.get(linha.ordemServicoId) ?? []), linha]);
  }

  if (isLoading) return <Skeleton className="h-32 w-full" />;
  if (porOs.size === 0)
    return (
      <EmptyState
        title="Sem itens"
        description="Nenhuma bancada de capacidade aplicada a esta medição."
      />
    );

  return (
    <div className="space-y-4">
      {[...porOs.entries()].map(([osId, bancadas]) => (
        <Card key={osId}>
          <CardContent className="space-y-2 p-4">
            <h3 className="font-medium">{bancadas[0]?.placa}</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-1">Tipo</th>
                  <th>Início vigência</th>
                  <th>Fim vigência</th>
                  <th className="text-right">Lugares totais</th>
                  <th className="text-right">Descontados</th>
                  <th className="text-right">Efetivos</th>
                  <th>Status vistoria</th>
                </tr>
              </thead>
              <tbody>
                {bancadas.map((b, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="py-1">{b.tipo}</td>
                    <td>{formatarData(b.inicioVigencia)}</td>
                    <td>{formatarData(b.fimVigencia)}</td>
                    <td className="text-right">{b.lugaresTotais}</td>
                    <td className="text-right">{b.descontados}</td>
                    <td className="text-right">{b.efetivos}</td>
                    <td>
                      <Badge variant="outline">{b.statusVistoria}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function AbaHistorico({ medicaoId }: { medicaoId: string }) {
  const { data, isLoading } = useMedicaoHistorico(medicaoId);
  const eventos: (AlteracaoHistoricoMedicao & { id: string })[] = (data ?? []).map((h) => ({
    ...h,
    id: h.id,
  }));

  if (isLoading) return <Skeleton className="h-32 w-full" />;
  if (eventos.length === 0) {
    return (
      <EmptyState
        icon={History}
        title="Sem alterações registradas"
        description="Nenhuma alteração pós-envio foi feita nesta medição."
      />
    );
  }

  return (
    <ol className="space-y-3">
      {eventos.map((ev) => (
        <li key={ev.id} className="border-l-2 border-border pl-4 text-sm">
          <p className="font-medium">{ev.campo}</p>
          <p className="text-muted-foreground">
            {ev.valorAnterior} → {ev.valorNovo}
          </p>
          <p className="text-xs text-muted-foreground">
            {ev.autorNome} ({ev.autorPapel}) · {formatarData(ev.dataHora, true)}
          </p>
          {ev.motivo && <p className="text-xs italic">{ev.motivo}</p>}
        </li>
      ))}
    </ol>
  );
}

export default MedicaoDetalhePage;
